/**
 * SocialLinksAdminPage — Platform admin CRUD editor for footer social media links.
 * Route: /admin/social-links (admin-only via ProtectedRoute + is_admin check)
 * API:   /api/admin/social-links (GET, POST, PATCH, DELETE)
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  Instagram, Youtube, Linkedin, Facebook, Twitter, Music2, Github, Twitch,
  MessageCircle, Send, Rss, Link as LinkIcon, Plus, Save, Trash2, ArrowLeft, Loader2,
} from 'lucide-react';

const ICON_MAP = {
  instagram: Instagram,
  youtube: Youtube,
  linkedin: Linkedin,
  facebook: Facebook,
  twitter: Twitter,
  x: Twitter,
  tiktok: Music2,
  github: Github,
  twitch: Twitch,
  discord: MessageCircle,
  telegram: Send,
  rss: Rss,
};

const ICON_OPTIONS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'twitter', label: 'X / Twitter' },
  { value: 'github', label: 'GitHub' },
  { value: 'twitch', label: 'Twitch' },
  { value: 'discord', label: 'Discord' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'rss', label: 'RSS Feed' },
  { value: 'link', label: 'Other (generic link)' },
];

const emptyDraft = {
  platform: '',
  label: '',
  url: '',
  icon: 'link',
  order: 100,
  enabled: false,
};

const IconPreview = ({ icon, size = 'w-5 h-5' }) => {
  const Cmp = ICON_MAP[icon] || LinkIcon;
  return <Cmp className={size} />;
};

const SocialLinksAdminPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);

  const isAdmin = !!user?.is_admin;

  useEffect(() => {
    if (!isAdmin) return;
    const load = async () => {
      try {
        const res = await api.get('/admin/social-links');
        setLinks(res.data.links || []);
      } catch (err) {
        toast.error('Failed to load social links');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [api, isAdmin]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <p className="text-slate-600">Admins only.</p>
      </div>
    );
  }

  const patchLink = async (id, patch) => {
    setSaving((s) => ({ ...s, [id]: true }));
    try {
      const res = await api.patch(`/admin/social-links/${id}`, patch);
      setLinks((prev) => prev.map((l) => (l.id === id ? res.data.link : l)));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setSaving((s) => ({ ...s, [id]: false }));
    }
  };

  const deleteLink = async (id) => {
    if (!window.confirm('Delete this social link?')) return;
    try {
      await api.delete(`/admin/social-links/${id}`);
      setLinks((prev) => prev.filter((l) => l.id !== id));
      toast.success('Deleted');
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const createLink = async () => {
    if (!draft.platform.trim() || !draft.label.trim()) {
      toast.error('Platform and label are required');
      return;
    }
    setCreating(true);
    try {
      const res = await api.post('/admin/social-links', draft);
      setLinks((prev) => [...prev, res.data.link].sort((a, b) => (a.order || 0) - (b.order || 0)));
      setDraft(emptyDraft);
      toast.success('Social link added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="social-links-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Social Footer Links</h1>
            <p className="text-sm text-slate-500">Update the icons that appear in the marketing site footer.</p>
          </div>
        </div>

        {/* Existing links */}
        <Card>
          <CardContent className="p-4">
            <h2 className="text-sm font-semibold text-slate-800 mb-3">Existing links</h2>
            {loading ? (
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : links.length === 0 ? (
              <p className="text-sm text-slate-500">No links yet — add one below.</p>
            ) : (
              <ul className="space-y-3" data-testid="social-links-list">
                {links.map((l) => (
                  <li key={l.id} className="border border-slate-200 rounded-lg p-3" data-testid={`social-link-row-${l.platform}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 shrink-0">
                        <IconPreview icon={l.icon} />
                      </div>
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Label</label>
                          <Input
                            defaultValue={l.label}
                            onBlur={(e) => e.target.value !== l.label && patchLink(l.id, { label: e.target.value })}
                            data-testid={`social-link-label-${l.platform}`}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">URL</label>
                          <Input
                            type="url"
                            defaultValue={l.url}
                            placeholder="https://…"
                            onBlur={(e) => e.target.value !== l.url && patchLink(l.id, { url: e.target.value })}
                            data-testid={`social-link-url-${l.platform}`}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Icon</label>
                          <select
                            defaultValue={l.icon}
                            onChange={(e) => patchLink(l.id, { icon: e.target.value })}
                            className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                            data-testid={`social-link-icon-${l.platform}`}
                          >
                            {ICON_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Order</label>
                          <Input
                            type="number"
                            defaultValue={l.order}
                            onBlur={(e) => Number(e.target.value) !== l.order && patchLink(l.id, { order: Number(e.target.value) || 0 })}
                            data-testid={`social-link-order-${l.platform}`}
                          />
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] text-slate-500">{l.enabled ? 'Live' : 'Hidden'}</span>
                          <Switch
                            checked={!!l.enabled}
                            onCheckedChange={(v) => patchLink(l.id, { enabled: v })}
                            data-testid={`social-link-enabled-${l.platform}`}
                          />
                        </div>
                        {saving[l.id] && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteLink(l.id)}
                          className="text-rose-500 hover:text-rose-700"
                          data-testid={`social-link-delete-${l.platform}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Add new */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add another platform
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Platform id</label>
                <Input
                  value={draft.platform}
                  onChange={(e) => setDraft({ ...draft, platform: e.target.value })}
                  placeholder="e.g. threads"
                  data-testid="social-link-new-platform"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Label</label>
                <Input
                  value={draft.label}
                  onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                  placeholder="Threads"
                  data-testid="social-link-new-label"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">URL</label>
                <Input
                  type="url"
                  value={draft.url}
                  onChange={(e) => setDraft({ ...draft, url: e.target.value })}
                  placeholder="https://…"
                  data-testid="social-link-new-url"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Icon</label>
                <select
                  value={draft.icon}
                  onChange={(e) => setDraft({ ...draft, icon: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                  data-testid="social-link-new-icon"
                >
                  {ICON_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Order</label>
                <Input
                  type="number"
                  value={draft.order}
                  onChange={(e) => setDraft({ ...draft, order: Number(e.target.value) || 0 })}
                  data-testid="social-link-new-order"
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch
                  checked={draft.enabled}
                  onCheckedChange={(v) => setDraft({ ...draft, enabled: v })}
                  data-testid="social-link-new-enabled"
                />
                <span className="text-sm text-slate-600">Show on site immediately</span>
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={createLink} disabled={creating} className="bg-emerald-500 hover:bg-emerald-600" data-testid="social-link-add-btn">
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                Add link
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SocialLinksAdminPage;
